import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      setSearchQuery('');
      setSearchOpen(false);
      setMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'WEDDING STORIES', path: '/wedding-stories' },
    { label: 'THE MAGAZINE', path: '/the-magazine' },
    { label: 'TIPS & INSPIRATION', path: '/tips-inspiration' },
    { label: 'OUR PARTNERS', path: '/partners' },
    { label: 'COUNTDOWN', path: '/countdown' },
    { label: 'ADVERTISE', path: '/advertise' },
    { label: 'ABOUT', path: '/about' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md' : ''}`} style={{ backgroundColor: '#FAF9F7' }}>
      {/* Logo */}
      <div className="py-5 border-b border-[#E6E2DC]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/" className="block text-center" data-testid="header-logo">
            <h1
              className="text-4xl md:text-5xl font-light tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}
            >
              I DO
            </h1>
            <p className="text-[10px] tracking-[0.3em] mt-0.5 uppercase" style={{ color: '#757B7F' }}>
              Bridal Magazine Malta
            </p>
          </Link>
        </div>
      </div>

      {/* Navigation */}
      <div className="border-b border-[#E6E2DC]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-12">
            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8 mx-auto" data-testid="desktop-nav">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-[11px] tracking-[0.15em] font-medium transition-colors duration-200 py-3 border-b-2 ${
                    isActive(item.path)
                      ? 'border-[#DE92A9] text-[#DE92A9]'
                      : 'border-transparent text-[#757B7F] hover:text-[#DE92A9]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="text-[11px] tracking-[0.15em] font-semibold text-white px-6 py-2 transition-all duration-200 hover:opacity-90"
                style={{ backgroundColor: '#DE92A9' }}
                data-testid="nav-contact-btn"
              >
                CONTACT
              </Link>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`p-2 transition-colors duration-200 ${searchOpen ? 'text-[#DE92A9]' : 'text-[#757B7F] hover:text-[#DE92A9]'}`}
                aria-label="Search the site"
                data-testid="header-search-toggle"
              >
                {searchOpen ? <X size={16} /> : <Search size={16} />}
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden ml-auto p-2"
              style={{ color: '#2C2C2C' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Desktop Search Bar */}
        {searchOpen && (
          <div className="hidden lg:block border-t border-[#E6E2DC]/60 animate-fade-in" style={{ backgroundColor: '#FAF9F7' }}>
            <div className="max-w-3xl mx-auto px-6 py-4">
              <form onSubmit={handleSearchSubmit} className="relative" data-testid="header-search-form">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                  placeholder="Search weddings, articles, venues, partners..."
                  className="w-full border border-[#E6E2DC] bg-white px-5 py-3 pr-12 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#DE92A9] transition-colors"
                  data-testid="header-search-input"
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#DE92A9' }}
                  aria-label="Submit search"
                  data-testid="header-search-submit"
                >
                  <Search size={14} />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E6E2DC] animate-fade-in" style={{ backgroundColor: '#FAF9F7' }}>
            <div className="px-6 py-4 space-y-1">
              <form onSubmit={handleSearchSubmit} className="relative mb-3" data-testid="mobile-search-form">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search the site..."
                  className="w-full border border-[#E6E2DC] bg-white px-4 py-3 pr-12 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#DE92A9]"
                  data-testid="mobile-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 text-white"
                  style={{ backgroundColor: '#DE92A9' }}
                  aria-label="Submit search"
                  data-testid="mobile-search-submit"
                >
                  <Search size={14} />
                </button>
              </form>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block text-sm tracking-wider py-3 border-b border-[#E6E2DC]/40 ${
                    isActive(item.path) ? 'text-[#DE92A9] font-medium' : 'text-[#757B7F]'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="block text-center text-sm tracking-wider font-semibold text-white py-3 mt-3"
                style={{ backgroundColor: '#DE92A9' }}
                data-testid="mobile-nav-contact-btn"
              >
                CONTACT US
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
